import { skipToken, useQuery } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import type {
  PurchaseOrder,
  ListPurchaseOrdersInput,
  ListPurchaseOrdersResponse,
  GetPurchaseOrderResponse,
  GetPurchaseOrderLineItemResponse,
} from '@erp/rpc';

export function usePurchaseOrdersList(filters: ListPurchaseOrdersInput = {}) {
  const query = useQuery(
    orpc.accountsPayable.purchaseOrders.list.queryOptions({ input: filters }),
  );

  const purchaseOrders = query.data?.purchaseOrders ?? [];
  const pagination = query.data?.pagination;

  return {
    ...query,
    purchaseOrders,
    pagination,
  } satisfies typeof query & {
    data?: ListPurchaseOrdersResponse;
    purchaseOrders: PurchaseOrder[];
    pagination: ListPurchaseOrdersResponse['pagination'] | undefined;
  };
}

export function usePurchaseOrder(id: string | undefined, enabled = true) {
  const query = useQuery({
    ...orpc.accountsPayable.purchaseOrders.get.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return {
    ...query,
    purchaseOrder: query.data?.purchaseOrder ?? null,
  } satisfies typeof query & {
    data?: GetPurchaseOrderResponse;
    purchaseOrder: GetPurchaseOrderResponse['purchaseOrder'] | null;
  };
}

export function usePurchaseOrderLineItem(
  id: string | undefined,
  enabled = true,
) {
  const query = useQuery({
    ...orpc.accountsPayable.purchaseOrders.getLineItem.queryOptions({
      input: id ? { id } : skipToken,
    }),
    enabled: Boolean(id) && enabled,
  });

  return {
    ...query,
    lineItem: query.data?.lineItem ?? null,
    purchaseOrder: query.data?.purchaseOrder ?? null,
  } satisfies typeof query & {
    data?: GetPurchaseOrderLineItemResponse;
    lineItem: GetPurchaseOrderLineItemResponse['lineItem'] | null;
    purchaseOrder: GetPurchaseOrderLineItemResponse['purchaseOrder'] | null;
  };
}
